import { useMemo } from 'react';
import { RoiAggregatesRespData } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { messagingOverviewDemoData } from '../common-demo-data/messaging-overview';
import { commonApiOptions } from '../helpers/api-helpers';
import { useRoiRequestPayload, UseRoiRequestProps } from './use-roi-request-payload';

type Aggregated = {
  [K in Exclude<keyof RoiAggregatesRespData, 'locationId'>]?: RoiAggregatesRespData[K];
};

interface UseFetchMessagingROIOOverview {
  aggregated: Aggregated;
  isLoading?: boolean;
  locationsData: {
    [locationId: string]: RoiAggregatesRespData & { locationName?: string };
  };
}

export const useFetchMessagingROIOOverview = (props: UseRoiRequestProps): UseFetchMessagingROIOOverview => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { accessibleLocationData } = useAppScopeStore();
  const { request, skipRequest } = useRoiRequestPayload(props);

  const { data: overviewData, isLoading } = AnalyticsGatewayV1.Queries.useGetRoiAggregatesQuery({
    request,
    options: {
      ...commonApiOptions,
      enabled: !skipRequest,
      onError: () => {
        alert.error(t("Couldn't fetch the overview details. Please try again."));
      },
    },
  });

  const processedData = useMemo(() => {
    const dataToUse = props.isDemoAccount ? messagingOverviewDemoData(props.filters?.locationIds || []) : overviewData;

    if (!dataToUse) {
      return {
        aggregated: {},
        isLoading: false,
        locationsData: {},
      };
    }

    const data = dataToUse.data;

    const locationsData = accessibleLocationData;
    const aggregated: UseFetchMessagingROIOOverview['aggregated'] = {};

    const locations = data?.reduce<UseFetchMessagingROIOOverview['locationsData']>((acc = {}, item) => {
      aggregated.appointmentsCompleted = {
        appointmentsCount:
          (aggregated.appointmentsCompleted?.appointmentsCount || 0) +
          (item.appointmentsCompleted?.appointmentsCount || 0),
        existingPatientsApptsCounts:
          (aggregated.appointmentsCompleted?.existingPatientsApptsCounts || 0) +
          (item.appointmentsCompleted?.existingPatientsApptsCounts || 0),
        newPatientsApptsCounts:
          (aggregated.appointmentsCompleted?.newPatientsApptsCounts || 0) +
          (item.appointmentsCompleted?.newPatientsApptsCounts || 0),
      };

      aggregated.leadsContacted = {
        existingPatientsCounts:
          (aggregated.leadsContacted?.existingPatientsCounts || 0) + (item.leadsContacted?.existingPatientsCounts || 0),
        newPatientsCounts:
          (aggregated.leadsContacted?.newPatientsCounts || 0) + (item.leadsContacted?.newPatientsCounts || 0),
        smsCount: (aggregated.leadsContacted?.smsCount || 0) + (item.leadsContacted?.smsCount || 0),
        unscheduledPatientsCounts:
          (aggregated.leadsContacted?.unscheduledPatientsCounts || 0) +
          (item.leadsContacted?.unscheduledPatientsCounts || 0),
      };

      aggregated.patientsScheduled = {
        appointmentsCount:
          (aggregated.patientsScheduled?.appointmentsCount || 0) + (item.patientsScheduled?.appointmentsCount || 0),
        existingPatientsCounts:
          (aggregated.patientsScheduled?.existingPatientsCounts || 0) +
          (item.patientsScheduled?.existingPatientsCounts || 0),
        newPatientsCounts:
          (aggregated.patientsScheduled?.newPatientsCounts || 0) + (item.patientsScheduled?.newPatientsCounts || 0),
      };

      if (item.locationId) {
        acc[item.locationId] = {
          ...item,
          locationName: locationsData[item.locationId]?.name,
        };
      }

      return acc;
    }, {});

    return {
      aggregated,
      locationsData: locations || {},
    };
  }, [overviewData, props.isDemoAccount, props.filters?.locationIds]);

  return {
    ...processedData,
    isLoading,
  };
};
